import type { ColumnMapping } from "@/lib/csv-to-beancount";
import type { DateOrder } from "@/lib/import-normalize";

/**
 * The verified export format for each bank or budgeting app: the column
 * mapping plus the conventions a CSV can't state.
 *
 * Deliberately **not** in `src/config/csv-bank-presets.ts`. That module is
 * imported by the route's `*.meta.ts`, which is eagerly reachable from the
 * entry chunk — carrying the sample exports along with it put the entry bundle
 * 1.4 KB over budget. The lander page imports this; nothing in the entry graph
 * does.
 */
export interface CsvBankFormat {
  /** Column mapping verified against a real export from this bank. */
  mapping: ColumnMapping;
  /** How this bank writes ambiguous dates. Omit when its dates are ISO. */
  dateOrder?: DateOrder;
  /** True when the export writes outflows as positive numbers. */
  invertAmounts?: boolean;
  /**
   * Column stating the direction, when `mapping.amount` is an unsigned
   * magnitude. Mint is the one export here shaped that way.
   */
  signColumn?: number;
  /** Values in {@link signColumn} that mean money out. */
  debitValues?: readonly string[];
  /** Default commodity, when the bank exports a single currency. */
  currency?: string;
  /**
   * A short excerpt in this bank's exact export shape — the header row it
   * really emits plus a few rows. Doubles as the "try it" sample and as the
   * fixture the integrity test maps to prove the preset still parses.
   */
  sampleCsv: string;
}

export const CSV_BANK_FORMATS: Record<string, CsvBankFormat> = {
  "chase": {
    // Chase's personal-checking export: Details,Posting Date,Description,
    // Amount,Type,Balance,Check or Slip #
    mapping: {
      date: 1,
      description: 2,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `Details,Posting Date,Description,Amount,Type,Balance,Check or Slip #
DEBIT,08/01/2026,"WHOLE FOODS MKT, AUSTIN TX",-84.23,DEBIT_CARD,4915.77,
DEBIT,08/02/2026,MONTHLY RENT,-1650.00,ACH_DEBIT,3265.77,
CREDIT,08/03/2026,ACME CORP PAYROLL,3200.00,ACH_CREDIT,6465.77,
`,
  },
  "amex": {
    // Amex's web export: Date,Description,Card Member,Account #,Amount
    mapping: {
      date: 0,
      description: 1,
      amount: 4,
      debit: null,
      credit: null,
      currency: null,
    },
    // Amex is a card issuer, so it writes what you *owe*: a purchase is a
    // positive number. Every other export here writes spending negative.
    dateOrder: "mdy",
    invertAmounts: true,
    currency: "USD",
    sampleCsv: `Date,Description,Card Member,Account #,Amount
08/01/2026,WHOLE FOODS MKT AUSTIN TX,JANE Q PUBLIC,-91007,84.23
08/02/2026,DELTA AIR LINES ATLANTA GA,JANE Q PUBLIC,-91007,412.60
08/04/2026,ONLINE PAYMENT - THANK YOU,JANE Q PUBLIC,-91007,-500.00
`,
  },
  "capital-one": {
    // Transaction Date,Posted Date,Card No.,Description,Category,Debit,Credit
    mapping: {
      date: 1,
      description: 3,
      amount: null,
      debit: 5,
      credit: 6,
      currency: null,
      category: 4,
    },
    currency: "USD",
    sampleCsv: `Transaction Date,Posted Date,Card No.,Description,Category,Debit,Credit
2026-07-31,2026-08-01,1234,WHOLE FOODS MKT,Dining,84.23,
2026-08-02,2026-08-02,1234,DELTA AIR LINES,Travel,412.60,
2026-08-04,2026-08-04,1234,CAPITAL ONE AUTOPAY PYMT,Payment/Credit,,500.00
`,
  },
  "bank-of-america": {
    // Date,Description,Amount,Running Bal. — after the summary preamble.
    mapping: {
      date: 0,
      description: 1,
      amount: 2,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `Date,Description,Amount,Running Bal.
08/01/2026,"WHOLE FOODS MKT, AUSTIN TX",-84.23,4915.77
08/02/2026,MONTHLY RENT,-1650.00,3265.77
08/03/2026,ACME CORP DES:PAYROLL,3200.00,6465.77
`,
  },
  "wells-fargo": {
    // Wells Fargo exports five columns and **no header row**:
    // date, amount, "*", (blank), description.
    mapping: {
      date: 0,
      description: 4,
      amount: 1,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `"08/01/2026","-84.23","*","","WHOLE FOODS MKT AUSTIN TX"
"08/02/2026","-1650.00","*","","MONTHLY RENT ONLINE PMT"
"08/03/2026","3200.00","*","","ACME CORP PAYROLL"
`,
  },
  "wise": {
    // Wise statement CSV, one file per currency balance.
    mapping: {
      date: 1,
      description: 4,
      amount: 2,
      debit: null,
      credit: null,
      currency: 3,
    },
    dateOrder: "dmy",
    sampleCsv: `TransferWise ID,Date,Amount,Currency,Description,Payment Reference,Running Balance,Exchange From,Exchange To,Exchange Rate
CARD-1234567,01-08-2026,-84.23,USD,Card transaction of 84.23 USD issued by Whole Foods,,4915.77,,,
TRANSFER-234567,02-08-2026,-1650.00,USD,Sent money to Landlord LLC,Rent August,3265.77,,,
TRANSFER-345678,03-08-2026,3200.00,USD,Received money from ACME CORP,Payroll,6465.77,,,
`,
  },
  "fidelity": {
    // The per-account History download. Two shapes come off the same screen —
    // 12 columns for a Cash Management account (`Security Description`) and 13
    // for a brokerage (`Description`, plus `Cash Balance ($)`) — and `Run Date`,
    // `Action` and `Amount ($)` sit at 0, 1 and 10 in both, which is what lets
    // one preset cover both. The all-accounts download is an 18-column file
    // with `Amount` elsewhere; the copy sends readers away from it.
    mapping: {
      date: 0,
      // `Action`, not `Security Description`: the latter is " No Description"
      // on every cash row, and auto-detection picks it. Mapping `Action` is the
      // clearest thing a preset buys on this export.
      description: 1,
      amount: 10,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `Run Date,Action,Symbol,Security Description,Security Type,Quantity,Price ($),Commission ($),Fees ($),Accrued Interest ($),Amount ($),Settlement Date
08/01/2026, DEBIT CARD PURCHASE WHOLE FOODS MKT AUSTIN TX (Cash),  , No Description,Cash,,,,,,-84.23,
08/02/2026, DIRECT DEBIT LANDLORD LLC RENT (Cash),  , No Description,Cash,,,,,,-1650.00,
08/03/2026, DIRECT DEPOSIT ACME CORP PAYROLL (Cash),  , No Description,Cash,,,,,,3200.00,
`,
  },
  "schwab": {
    // The brokerage Transactions export, which is what "Schwab CSV" means.
    // Schwab Bank checking is a different file (`Withdrawal`/`Deposit` split
    // columns) whose 2020 and 2022 shapes are not index-compatible with each
    // other, so it is answered in the FAQ with column *names* rather than
    // mapped here.
    mapping: {
      date: 0,
      description: 3,
      amount: 7,
      debit: null,
      credit: null,
      currency: null,
    },
    // Amounts are written `-$995.00` and `"$1,000.00"` — minus outside the
    // symbol, thousands separators inside. `parseAmount` reads both.
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `"Date","Action","Symbol","Description","Quantity","Price","Fees & Comm","Amount"
"08/01/2026","MoneyLink Transfer","","Transfer from Chase Checking","","","","$1,000.00"
"08/02/2026","Buy","VTI","VANGUARD TOTAL STOCK MKT ETF","4","$248.75","$0.00","-$995.00"
"08/03/2026","Cash Dividend","SWVXX","SCHWAB VALUE ADVANTAGE MONEY INV","","","","$12.48"
`,
  },
  "citi": {
    // Status,Date,Description,Debit,Credit — cards with named cardmembers
    // append `Member Name`, which leaves the four mapped columns where they are.
    mapping: {
      date: 1,
      description: 2,
      amount: null,
      // Citi signs its `Credit` column (`-41.00`) and leaves `Debit` a positive
      // magnitude, which is the opposite of most split exports. The split path
      // forces `-abs`/`+abs` per role, so the mixture converts correctly either
      // way — this is the preset where that matters.
      debit: 3,
      credit: 4,
      currency: null,
    },
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `Status,Date,Description,Debit,Credit,Member Name
Cleared,08/01/2026,"WHOLE FOODS MKT, AUSTIN TX",84.23,,JANE Q PUBLIC
Cleared,08/02/2026,"DELTA AIR LINES ATLANTA GA",412.60,,JANE Q PUBLIC
Cleared,08/04/2026,"ONLINE PAYMENT, THANK YOU",,-500.00,JANE Q PUBLIC
`,
  },
  "discover": {
    // Trans. Date,Post Date,Description,Amount,Category
    mapping: {
      // The purchase date, not the settlement date — it is what a receipt says.
      date: 0,
      description: 2,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
      category: 4,
    },
    // Like Amex, Discover writes what you owe: a purchase is `54.93` and a
    // payment `-1502.74`. The reference importer does exactly one thing to the
    // column — multiply by -1.
    dateOrder: "mdy",
    invertAmounts: true,
    currency: "USD",
    sampleCsv: `Trans. Date,Post Date,Description,Amount,Category
08/01/2026,08/02/2026,"WHOLE FOODS MKT, AUSTIN TX",84.23,"Supermarkets"
08/02/2026,08/03/2026,"DELTA AIR LINES ATLANTA GA",412.60,"Travel/ Entertainment"
08/04/2026,08/04/2026,"INTERNET PAYMENT - THANK YOU",-500.00,"Payments and Credits"
`,
  },
  "ally": {
    // `Date, Time, Amount, Type, Description` — Ally writes a space after each
    // comma; `parseCsv` trims header cells, so the indices are unaffected.
    mapping: {
      date: 0,
      description: 4,
      amount: 2,
      debit: null,
      credit: null,
      currency: null,
    },
    // No `dateOrder`: Ally exports ISO dates, and keeps the clock in its own
    // `Time` column, so the date cell is never ambiguous.
    currency: "USD",
    sampleCsv: `Date, Time, Amount, Type, Description
2026-08-01,09:12:33,-84.23,Withdrawal,"WHOLE FOODS MKT, AUSTIN, TX, US"
2026-08-02,07:00:00,-1650.00,Withdrawal,LANDLORD LLC RENT ~ Tran: ACHDW
2026-08-03,06:00:00,3200.00,Deposit,ACME CORP DIRECT DEP
`,
  },
  "monzo": {
    // 18 columns; a variant appends `Balance,Balance currency`, which leaves
    // the first 18 — and so every mapped index — in place.
    mapping: {
      date: 1,
      // `Name` is Monzo's cleaned merchant name; `Description` (14) is the raw
      // bank descriptor, one select away. Same choice as Mint.
      description: 4,
      // `Amount`, never `Local amount` (9): a €89.99 purchase is -78.30 GBP
      // here and -89.99 EUR there, and only this one moved the balance.
      amount: 7,
      debit: null,
      credit: null,
      currency: 8,
      category: 6,
    },
    // Day-first, and this is the field that matters most in the preset: read
    // month-first, `01/08/2026` lands in January and nothing visibly fails. A
    // US Monzo account exports month-first instead — the copy says so.
    dateOrder: "dmy",
    // No `currency` default — the file states it per row, like Wise.
    sampleCsv: `Transaction ID,Date,Time,Type,Name,Emoji,Category,Amount,Currency,Local amount,Local currency,Notes and #tags,Address,Receipt,Description,Category split,Money Out,Money In
tx_0000AbCdEfGhIjKlMnOpQr,01/08/2026,09:12:33,Card payment,Sainsbury's,,Groceries,-84.23,GBP,-84.23,GBP,,24-26 Trinity Street,,SAINSBURYS COVENTRY GBR,,-84.23,
tx_0000AbCdEfGhIjKlMnOpQs,02/08/2026,07:00:00,Faster payment,Landlord LLC,,Bills,-1650.00,GBP,-1650.00,GBP,August rent,,,LANDLORD LLC,,-1650.00,
tx_0000AbCdEfGhIjKlMnOpQt,03/08/2026,06:00:00,Faster payment,ACME CORP,,Income,3200.00,GBP,3200.00,GBP,,,,ACME CORP SALARY,,,3200.00
`,
  },
  "us-bank": {
    // "Date","Transaction","Name","Memo","Amount" — the same five columns in
    // every sampled export from 2016 to 2026.
    mapping: {
      date: 0,
      // `Name`, not `Memo`: the memo is filler (`Download from usbank.com.` on
      // a checking export, a `; ; ; ;`-padded acquirer reference on a card
      // one), and the payee lives in `Name`.
      description: 2,
      amount: 4,
      debit: null,
      credit: null,
      currency: null,
    },
    // Declared, not detected. A short export is genuinely undecidable — a real
    // 47-row December statement spans 12/1–12/7, so no day exceeds 12 and
    // `detectDateOrder` returns "ambiguous". Read day-first it converts
    // cleanly into seven *different* months and nothing looks wrong.
    // The current (2026) export writes ISO dates instead; `normalizeDate`
    // reads those regardless of the declared order, so one preset covers both.
    dateOrder: "mdy",
    // Signed on both product lines — a card purchase is negative and a payment
    // positive, unlike Amex/Discover/Apple Card. No inversion, no sign column.
    currency: "USD",
    // Four decimal places, which is what the export really carries: the reader
    // sees `-84.2300 USD` and has to be told it isn't a bug. A waived fee
    // exports as `0.0000` and converts to a zero-value transaction.
    sampleCsv: `"Date","Transaction","Name","Memo","Amount"
"8/1/2026","DEBIT","DEBIT PURCHASE -VISA WHOLE FOODS MKT AUSTIN TX","Download from usbank.com. WHOLE FOODS MKT AUSTIN TX","-84.2300"
"8/2/2026","DEBIT","INTERNET BANKING TRANSFER TO LANDLORD LLC","Download from usbank.com. LANDLORD LLC","-1650.0000"
"8/3/2026","CREDIT","ELECTRONIC DEPOSIT ACME CORP PAYROLL","Download from usbank.com. ACME CORP PAYROLL","3200.0000"
`,
  },
  "td-canada-trust": {
    // EasyWeb's `accountactivity.csv`, and the reason this lander earns its
    // place: five columns and **no header row at all**, so detection has
    // nothing to read names off. Unaided it claims `amount: 2` — the debit
    // column alone — which posts every withdrawal as money *in* and reports
    // every deposit as an unrecognized amount.
    mapping: {
      date: 0,
      description: 1,
      amount: null,
      debit: 2,
      credit: 3,
      currency: null,
    },
    // `MM/DD/YYYY` in every sampled export. A community importer updated in
    // 2025 accepts `YYYY-MM-DD` as a newer TD shape, which is order-independent
    // and converts identically under this declaration.
    dateOrder: "mdy",
    // TD Bank N.A. (US) is a different institution; every real sample of this
    // format is Canadian, so the lander says so and the commodity is CAD.
    currency: "CAD",
    // Descriptions are fixed-width and space-padded to 20 characters. The
    // trailing run is trimmed; the interior padding survives in the narration.
    sampleCsv: `08/01/2026,WHOLE FOODS MKT     ,84.23,,4915.77
08/02/2026,TD VISA      1234AB ,1650.00,,3265.77
08/03/2026,ACME CORP    PAY    ,,3200.00,6465.77
08/03/2026,CREDIT INTEREST     ,,0.02,6465.79
`,
  },
  "apple-card": {
    // Transaction Date,Clearing Date,Description,Merchant,Category,Type,
    // Amount (USD),Purchased By — one file per statement month.
    mapping: {
      date: 0,
      // `Merchant` (3), not `Description` (2). The description is the raw
      // authorization string carrying the merchant's street address; the
      // merchant column is the clean payee. Auto-detection picks the wordier
      // one, so this is the main thing the preset buys.
      description: 3,
      amount: 6,
      debit: null,
      credit: null,
      currency: null,
      category: 4,
    },
    dateOrder: "mdy",
    // Issuer-signed like Amex and Discover: a purchase is positive (what you
    // owe), a payment and a return negative.
    invertAmounts: true,
    currency: "USD",
    // `Purchased By` is Apple Card Family's per-cardholder column, present in
    // every sampled export. It sits after every mapped index, so a family file
    // and a single-cardholder file map identically.
    sampleCsv: `Transaction Date,Clearing Date,Description,Merchant,Category,Type,Amount (USD),Purchased By
08/01/2026,08/02/2026,"WHOLE FOODS MKT 4001 24TH ST SAN FRANCISCO 94114 CA USA","Whole Foods Market","Grocery","Purchase","84.23","Cardholder Name"
08/02/2026,08/03/2026,"DELTA AIR LINES 1030 DELTA BLVD ATLANTA 30354 GA USA","Delta Air Lines","Travel","Purchase","412.60","Cardholder Name"
08/03/2026,08/04/2026,"WHOLE FOODS MKT 4001 24TH ST SAN FRANCISCO 94114 CA USA (RETURN)","Whole Foods Market","Grocery","Credit","-31.40","Cardholder Name"
08/04/2026,08/04/2026,"ACH DEPOSIT INTERNET TRANSFER FROM ACCOUNT ENDING IN 1234","Ach Deposit Internet Transfer From Account Ending In 1234","Payment","Payment","-500.00","Cardholder Name"
`,
  },
  "barclays": {
    // Number,Date,Account,Amount,Subcategory,Memo — a UK current or business
    // account. Some exports add a trailing empty seventh column, which leaves
    // every mapped index where it is.
    mapping: {
      date: 1,
      // `Memo` is the payee; `Subcategory` (4) is Barclays' own transaction
      // type (`Direct Debit`, `Contactless Card Purchase`) and `Number` (0) a
      // cheque number, `0` on most rows.
      description: 5,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
    },
    // Day-first — the one non-US order in this round, and the failure is
    // silent: `01/08/2026` read month-first lands in January and converts
    // cleanly. `normalizeDate` self-corrects a row whose day exceeds 12, so a
    // month of statements can be half-right without erroring once.
    dateOrder: "dmy",
    // One signed column, money out negative; no debit/credit split. Whole
    // pounds arrive without decimals (`-110000`) and post as `-110000.00 GBP`.
    currency: "GBP",
    sampleCsv: `Number,Date,Account,Amount,Subcategory,Memo
941284,01/08/2026,20-00-00 12345678,-84.23,Contactless Card Purchase,WHOLE FOODS MKT
0,02/08/2026,20-00-00 12345678,-1650.00,Direct Debit,LANDLORD LLC
0,03/08/2026,20-00-00 12345678,3200.00,Counter Credit,ACME CORP LTD
`,
  },
  "ynab": {
    // YNAB's Register export (and the Selected-Transactions export, which is
    // byte-identical in its header): Account,Flag,Date,Payee,Category
    // Group/Category,Category Group,Category,Memo,Outflow,Inflow,Cleared.
    mapping: {
      date: 2,
      description: 3,
      amount: null,
      // Two magnitude columns, and the unused side is written `$0.00` rather
      // than left blank — so "the non-empty column wins" would be wrong.
      debit: 8,
      credit: 9,
      currency: null,
      // `Category` (6), not `Category Group` (5): the leaf is what a reader
      // assigns an account to.
      category: 6,
    },
    // US plans export MM/DD/YYYY; a European plan exports DD.MM.YYYY as TSV,
    // which auto-detects from the dots once real rows are pasted.
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `"Account","Flag","Date","Payee","Category Group/Category","Category Group","Category","Memo","Outflow","Inflow","Cleared"
"Checking","","08/01/2026","Whole Foods Market, Austin TX","Everyday Expenses: Groceries","Everyday Expenses","Groceries","Weekly shop","$84.23","$0.00","Cleared"
"Checking","","08/02/2026","Landlord LLC","Everyday Expenses: Rent","Everyday Expenses","Rent","August rent","$1,650.00","$0.00","Cleared"
"Checking","","08/03/2026","ACME Corp","Inflow: Ready to Assign","Inflow","Ready to Assign","Payroll","$0.00","$3,200.00","Cleared"
"Checking","","08/05/2026","Transfer : Savings","","","","Monthly transfer","$500.00","$0.00","Uncleared"
`,
  },
  "mint": {
    // The frozen Mint archive: Date,Description,Original Description,Amount,
    // Transaction Type,Category,Account Name,Labels,Notes.
    mapping: {
      date: 0,
      // Mint's cleaned merchant name; `Original Description` (2) is the raw
      // bank string, one select away for readers who prefer it.
      description: 1,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
      // (`Transaction Type` (4) beside it is the direction, not a category —
      // it stays a sign column.)
      category: 5,
    },
    // `Amount` is an unsigned magnitude in every row — the direction lives in
    // `Transaction Type`, which is why this preset needs a sign column and a
    // plain `amount: 3` mapping would invert every debit.
    signColumn: 4,
    debitValues: ["debit"],
    dateOrder: "mdy",
    currency: "USD",
    sampleCsv: `"Date","Description","Original Description","Amount","Transaction Type","Category","Account Name","Labels","Notes"
"8/1/2026","Whole Foods Market, Austin TX","WHOLEFDS AUS 10145","84.23","debit","Groceries","CHASE CHECKING","",""
"8/2/2026","Landlord LLC","ONLINE PMT TO LANDLORD LLC","1650.00","debit","Mortgage & Rent","CHASE CHECKING","",""
"8/3/2026","Acme Corp Payroll","ACME CORP DIRECT DEP","3200.00","credit","Paycheck","CHASE CHECKING","",""
`,
  },
  "monarch": {
    // Date,Merchant,Category,Account,Original Statement,Notes,Amount,Tags —
    // household plans append `Owner`, which leaves `Amount` at index 6.
    mapping: {
      date: 0,
      description: 1,
      amount: 6,
      debit: null,
      credit: null,
      currency: null,
      category: 2,
    },
    // No `dateOrder`: Monarch exports ISO dates.
    currency: "USD",
    sampleCsv: `Date,Merchant,Category,Account,Original Statement,Notes,Amount,Tags
2026-08-01,"Whole Foods Market, Austin TX",Groceries,Checking Account (...1234),WHOLEFDS AUS 10145,,-84.23,
2026-08-02,Landlord LLC,Rent,Checking Account (...1234),ONLINE PMT TO LANDLORD LLC,,-1650.00,
2026-08-03,Acme Corp,Paychecks,Checking Account (...1234),ACME CORP DIRECT DEP,,3200.00,
`,
  },
  "empower": {
    // Date,Account,Description,Category,Tags,Amount — the web dashboard's
    // transactions CSV. The sign is the change to the account, so a card
    // charge is already negative: no `invertAmounts`, unlike Amex.
    mapping: {
      date: 0,
      description: 2,
      amount: 5,
      debit: null,
      credit: null,
      currency: null,
      category: 3,
    },
    // No `dateOrder`: Empower exports ISO dates.
    currency: "USD",
    sampleCsv: `Date,Account,Description,Category,Tags,Amount
2026-08-01,"Checking - Ending in 1234","Whole Foods Market, Austin TX","Groceries",,-84.23
2026-08-02,"Checking - Ending in 1234","Online Pmt To Landlord Llc","Mortgage & Rent",,-1650.00
2026-08-03,"Checking - Ending in 1234","Acme Corp Payroll","Paychecks",,3200
`,
  },
  "n26": {
    // N26 web export: Date,Payee,Account,Amount,Type,Balance (EUR, day-first)
    mapping: {
      date: 0,
      description: 1,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "dmy",
    currency: "EUR",
    sampleCsv: `Date,Payee,Account,Amount,Type,Balance
01/08/2026,WHOLE FOODS MKT,DE89370400440532013000,-84.23,DEBIT,4915.77
02/08/2026,LANDLORD LLC,DE89370400440532013000,-1650.00,DEBIT,3265.77
03/08/2026,ACME CORP,DE89370400440532013000,3200.00,CREDIT,6465.77
`,
  },
  "starling": {
    // Starling Open Banking export: Date,Counter Party,Reference,Amount,Balance
    mapping: {
      date: 0,
      description: 1,
      amount: 3,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "dmy",
    currency: "GBP",
    sampleCsv: `Date,Counter Party,Reference,Amount,Balance
01/08/2026,WHOLE FOODS MKT,Groceries,-84.23,4915.77
02/08/2026,LANDLORD LLC,RENT,-1650.00,3265.77
03/08/2026,ACME CORP LTD,PAYROLL,3200.00,6465.77
`,
  },
  "bunq": {
    // bunq export: Date,Description,Amount,Account,Counterparty
    mapping: {
      date: 0,
      description: 1,
      amount: 2,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "dmy",
    currency: "EUR",
    sampleCsv: `Date,Description,Amount,Account,Counterparty
2026-08-01,WHOLE FOODS MKT,-84.23,NL90BUNQ1234567890,WHOLE FOODS MKT
2026-08-02,LANDLORD LLC,-1650.00,NL90BUNQ1234567890,LANDLORD LLC
2026-08-03,ACME CORP,3200.00,NL90BUNQ1234567890,ACME CORP
`,
  },
  "ing": {
    // ING (NL/DE) export: Date,Name / Description,Account,Code,Amount
    mapping: {
      date: 0,
      description: 1,
      amount: 4,
      debit: null,
      credit: null,
      currency: null,
    },
    dateOrder: "dmy",
    currency: "EUR",
    sampleCsv: `Date,Name / Description,Account,Code,Amount
01/08/2026,WHOLE FOODS MKT,NL90INGB1234567890,BA,-84.23
02/08/2026,LANDLORD LLC,NL90INGB1234567890,GT,-1650.00
03/08/2026,ACME CORP,NL90INGB1234567890,GT,3200.00
`,
  },
};
